import { useEffect, useState } from "react";
import { supabase } from "@/utils/supabase";
import { StoreManagement } from "@/utils/types";
import { Loader } from "@/components/loader";
import { getDashboardStoreHref } from "@/utils/store-routes";
import { Link } from "react-router-dom";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";

export default function DashboardPage() {
  const [openStores, setOpenStores] = useState<StoreManagement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOpenStores();
  }, []);

  const fetchOpenStores = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("store_management")
        .select("*")
        .eq("is_open", true)
        .order("display_name", { ascending: true });

      if (error) {
        console.error("Error fetching open stores:", error);
        setOpenStores([]);
        return;
      }

      // Filter stores that have passed their closing date
      const now = new Date();
      const filteredStores = (data || []).filter((store: StoreManagement) => {
        if (!store.is_open) return false;
        if (store.closing_date) {
          const closingDate = new Date(store.closing_date);
          return closingDate > now;
        }
        return true;
      }) as StoreManagement[];

      setOpenStores(filteredStores);
    } catch (err) {
      console.error("Error fetching open stores:", err);
      setOpenStores([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to your dashboard</p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Link
          to="/dashboard/profile"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200"
        >
          <div className="text-2xl mb-2">👤</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Profile</h3>
          <p className="text-sm text-gray-600">View and edit your profile</p>
        </Link>
        <Link
          to="/dashboard/my-races"
          className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200"
        >
          <div className="text-2xl mb-2">🏁</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">My Races</h3>
          <p className="text-sm text-gray-600">View your registered races</p>
        </Link>
        {openStores.map((store) => (
          <Link
            key={store.id}
            to={getDashboardStoreHref(store.store_name)}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200"
          >
            <div className="text-2xl mb-2">🛍️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {store.display_name}
            </h3>
            <p className="text-sm text-gray-600">
              {store.description || "Open store in dashboard"}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
